/**
 * Défilement horizontal infini (« marquee ») des rangées `[data-parallax]`
 * d'un host : chaque rangée est dupliquée pour boucler sans couture, puis
 * animée en translateX linéaire. Les rangées alternent le sens (0 → gauche,
 * 1 → droite, 2 → gauche…). Pause au survol. Rien ne bouge si
 * `prefers-reduced-motion: reduce`.
 */
export const MARQUEE_SPEED_PX_PER_S = 40;
export const MARQUEE_MIN_DURATION_MS = 1000;
export const MARQUEE_ROW_SELECTOR = "[data-parallax]";
export const MARQUEE_CLONE_ATTRIBUTE = "data-marquee-clone";

/** Sens d'une rangée : pair → normal (vers la gauche), impair → inverse. */
export function marqueeDirection(index: number): "normal" | "reverse" {
  return index % 2 === 0 ? "normal" : "reverse";
}

/**
 * Nombre de copies à ajouter pour que la piste couvre au moins deux fois la
 * largeur visible (une copie minimum : c'est elle qui permet la boucle).
 */
export function marqueeCopies(contentWidth: number, viewportWidth: number): number {
  if (contentWidth <= 0) return 0;
  return Math.max(1, Math.ceil((viewportWidth * 2) / contentWidth));
}

/** Durée d'un tour à vitesse constante (px/s), bornée pour éviter un flash. */
export function marqueeDurationMs(distancePx: number, speed = MARQUEE_SPEED_PX_PER_S): number {
  if (distancePx <= 0 || speed <= 0) return MARQUEE_MIN_DURATION_MS;
  return Math.max(MARQUEE_MIN_DURATION_MS, (distancePx / speed) * 1000);
}

function columnGapOf(element: Element | null): number {
  const view = element?.ownerDocument.defaultView;
  if (!element || !view) return 0;
  const gap = Number.parseFloat(view.getComputedStyle(element).columnGap);
  return Number.isFinite(gap) ? gap : 0;
}

/**
 * Espace entre deux copies : le gap de la rangée, sinon celui de son premier
 * enfant (rangée qui ne contient qu'une grille de cartes).
 */
export function rowGap(row: HTMLElement): number {
  return columnGapOf(row) || columnGapOf(row.firstElementChild);
}

export type MarqueeOptions = {
  /** Sélecteur des rangées à faire défiler (défaut : `[data-parallax]`, blocs capturés). */
  rowSelector?: string;
  /** Vitesse en px/s (défaut : 40). */
  speed?: number;
  /** `true` : ne force ni display:flex ni la largeur des enfants (rangée déjà disposée, ex. grille en colonnes). */
  keepLayout?: boolean;
};

export class MarqueeController {
  readonly root: HTMLElement;
  private readonly rows: HTMLElement[];
  private readonly media: MediaQueryList | null;
  private readonly speed: number;
  private readonly keepLayout: boolean;
  private animations: Animation[] = [];
  private cleanups: Array<() => void> = [];
  private rafId = 0;
  private destroyed = false;

  constructor(root: HTMLElement, options: MarqueeOptions = {}) {
    this.root = root;
    this.speed = options.speed ?? MARQUEE_SPEED_PX_PER_S;
    this.keepLayout = options.keepLayout ?? false;
    this.rows = Array.from(root.querySelectorAll<HTMLElement>(options.rowSelector ?? MARQUEE_ROW_SELECTOR));
    const view = root.ownerDocument.defaultView;
    this.media =
      typeof view?.matchMedia === "function"
        ? view.matchMedia("(prefers-reduced-motion: reduce)")
        : null;
    if (this.media?.matches) return;
    view?.addEventListener("resize", this.onResize, { passive: true });
    this.build();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    const view = this.root.ownerDocument.defaultView;
    view?.removeEventListener("resize", this.onResize);
    if (this.rafId !== 0) {
      view?.cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
    this.teardown();
  }

  private onResize = (): void => {
    if (this.destroyed || this.rafId !== 0) return;
    const view = this.root.ownerDocument.defaultView;
    if (!view) return;
    this.rafId = view.requestAnimationFrame(() => {
      this.rafId = 0;
      if (this.destroyed) return;
      this.teardown();
      this.build();
    });
  };

  private teardown(): void {
    for (const animation of this.animations) animation.cancel();
    this.animations = [];
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups = [];
    for (const row of this.rows) {
      for (const clone of Array.from(row.querySelectorAll(`[${MARQUEE_CLONE_ATTRIBUTE}]`))) {
        clone.remove();
      }
      for (const node of Array.from(row.children) as HTMLElement[]) {
        node.style.flex = "";
        node.style.width = "";
        node.style.maxWidth = "";
      }
      row.style.maxWidth = "";
      row.style.display = "";
      row.style.flexDirection = "";
      row.style.flexWrap = "";
      row.style.columnGap = "";
      row.style.width = "";
      row.style.willChange = "";
    }
  }

  private build(): void {
    const view = this.root.ownerDocument.defaultView;
    if (!view || typeof HTMLElement.prototype.animate !== "function") return;
    const viewportWidth = view.innerWidth;

    this.rows.forEach((row, index) => {
      const originals = Array.from(row.children) as HTMLElement[];
      if (originals.length === 0) return;
      const gap = rowGap(row);
      // Largeur naturelle de chaque élément, mesurée AVANT de toucher à la rangée :
      // pour une carte c'est sa largeur, pour une grille de cartes c'est la somme
      // de ses colonnes (scrollWidth), même si .bde-div la tronque à 100 %.
      const widths = originals.map((node) => node.scrollWidth);
      if (!this.keepLayout) {
        row.style.display = "flex";
        row.style.flexDirection = "row";
        row.style.flexWrap = "nowrap";
        row.style.columnGap = `${gap}px`;
        originals.forEach((node, i) => {
          node.style.flex = "0 0 auto";
          node.style.width = `${widths[i]}px`;
          node.style.maxWidth = "none"; // .bde-div impose max-width:100% : la grille serait tronquée
        });
      }
      row.style.width = "max-content";
      row.style.maxWidth = "none";
      // Distance d'un tour : la piste d'origine + l'espace avant la première copie.
      const distance = row.scrollWidth + gap;
      const copies = marqueeCopies(distance, viewportWidth);
      for (let copy = 0; copy < copies; copy += 1) {
        for (const node of originals) {
          const clone = node.cloneNode(true) as HTMLElement;
          clone.setAttribute(MARQUEE_CLONE_ATTRIBUTE, "");
          clone.setAttribute("aria-hidden", "true");
          row.append(clone);
        }
      }
      row.style.willChange = "transform";
      const animation = row.animate(
        [{ transform: "translate3d(0,0,0)" }, { transform: `translate3d(-${distance}px,0,0)` }],
        {
          duration: marqueeDurationMs(distance, this.speed),
          iterations: Number.POSITIVE_INFINITY,
          direction: marqueeDirection(index),
          easing: "linear",
        },
      );
      this.animations.push(animation);

      const pause = (): void => animation.pause();
      const play = (): void => animation.play();
      row.addEventListener("pointerenter", pause);
      row.addEventListener("pointerleave", play);
      this.cleanups.push(() => {
        row.removeEventListener("pointerenter", pause);
        row.removeEventListener("pointerleave", play);
      });
    });
  }
}
